/** Suppliers & Credentials — domain types (Sprint 1.1) */

export type SupplierIntegrationKind = 'api' | 'automation' | 'hybrid' | 'manual'

export type CredentialStatus =
  | 'CONNECTED'
  | 'DISCONNECTED'
  | 'ERROR'
  | 'INVALID_CREDENTIAL'

export type SupplierPermission = 'view' | 'edit' | 'delete'

export interface Supplier {
  id: string
  name: string
  code: string
  website: string | null
  logo: string | null
  supportsApi: boolean
  supportsAutomation: boolean
  supportsPix: boolean
  supportsInstallments: boolean
  active: boolean
  kind: SupplierIntegrationKind
  searchCount: number
  createdAt: string
  updatedAt: string
}

export interface SupplierCredential {
  id: string
  supplierId: string
  username: string | null
  /** AES-256 ciphertext — never plaintext */
  encryptedPassword: string | null
  apiKey: string | null
  apiSecret: string | null
  token: string | null
  refreshToken: string | null
  expiresAt: string | null
  lastLogin: string | null
  status: CredentialStatus
  createdAt: string
  updatedAt: string
}

/** Safe view model — password never exposed in clear text */
export interface SupplierCredentialView {
  id: string
  supplierId: string
  username: string | null
  passwordMasked: string
  hasPassword: boolean
  apiKeyMasked: string | null
  apiSecretMasked: string | null
  tokenMasked: string | null
  refreshTokenMasked: string | null
  expiresAt: string | null
  lastLogin: string | null
  status: CredentialStatus
  createdAt: string
  updatedAt: string
}

export interface SupplierCredentialInput {
  username?: string | null
  password?: string | null
  apiKey?: string | null
  apiSecret?: string | null
  token?: string | null
  refreshToken?: string | null
  expiresAt?: string | null
}

export interface SupplierInput {
  name: string
  code: string
  website?: string | null
  logo?: string | null
  supportsApi?: boolean
  supportsAutomation?: boolean
  supportsPix?: boolean
  supportsInstallments?: boolean
  active?: boolean
  kind?: SupplierIntegrationKind
}

export interface SupplierLog {
  id: string
  supplierId: string
  event: string
  message: string
  status: string
  createdAt: string
}

export interface SupplierAuditEntry {
  id: string
  supplierId: string
  field: string
  previousValue: string
  newValue: string
  changedBy: string
  changedAt: string
}

export interface SupplierFilters {
  query?: string
  connectedOnly?: boolean
  apiOnly?: boolean
  automationOnly?: boolean
  activeOnly?: boolean
  status?: CredentialStatus | 'ALL' | null
}

export interface SupplierPermissions {
  view: boolean
  edit: boolean
  delete: boolean
}

export interface SupplierDashboard {
  total: number
  connected: number
  disconnected: number
  needLogin: number
}

export interface ConnectionTestResult {
  ok: boolean
  status: CredentialStatus
  message: string
  testedAt: string
}

export interface DecryptedCredentials {
  username: string | null
  password: string | null
  apiKey: string | null
  apiSecret: string | null
  token: string | null
  refreshToken: string | null
}

export const CREDENTIAL_STATUS_LABELS: Record<CredentialStatus, string> = {
  CONNECTED: 'Conectado',
  DISCONNECTED: 'Desconectado',
  ERROR: 'Erro',
  INVALID_CREDENTIAL: 'Credencial Inválida',
}

export const DEFAULT_SUPPLIER_PERMISSIONS: SupplierPermissions = {
  view: true,
  edit: true,
  delete: true,
}
